import DropDown from "@/src/components/ui-kit/dropdown";
import { useAuth } from "@/src/contexts/auth.context";
import type { BlocksUser } from "@/src/models/user.model";
import {
  useActivateUserMutation,
  useAllUsersQuery,
  useDeactivateUserMutation,
  useDeleteUserMutation,
  useSendActivationToUser,
  useUsersQuery,
} from "@/src/services/users.blocks.api";
import { getUserOrgRoles } from "@/src/utils/role.utils";
import { useRouter } from "expo-router";
import { Send2, Trash, UserRemove, UserTick } from "iconsax-react-nativejs";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Image,
  Pressable,
  ScrollView,
  Text,
  View,
} from "react-native";
import { DataTable } from "react-native-paper";
import ConfirmModal from "../../action-modals/confirm-modal";
import ScPagination from "../../ui-kit/pagination";
import { Bone, SkeletonPulse } from "../../ui-kit/skeleton";
import { RoleBadgeCell, UserEmailCell, UserNameCell } from "./hover-cells";

type UserListingTableProps = Readonly<{
  search?: string;
  startDate?: string;
  endDate?: string;
  roleFilter?: string[] | null;
  statusFilter?: string | null;
  onFilteredCountChange?: (count: number) => void;
}>;

// Column widths, shared by the header and each row — must match the
// DataTable below (Name 3, Email 3, Status 2, Role 2, Joined 2, Actions 1).
const SKELETON_COLUMNS = [3, 3, 2, 2, 2, 1] as const;
const SKELETON_ROWS = 8;

function UserListingTableSkeleton() {
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{ flexGrow: 1, minWidth: 900 }}
    >
      <SkeletonPulse style={{ flex: 1 }} testID="user-listing-skeleton">
        {/* Header */}
        <View className="flex-row items-center px-4 py-4 gap-4 border-b border-ash-3">
          {SKELETON_COLUMNS.map((flex, i) => (
            <View key={i} style={{ flex }}>
              <Bone className="h-3 w-16" />
            </View>
          ))}
        </View>

        {Array.from({ length: SKELETON_ROWS }, (_, row) => (
          <View
            key={row}
            className="flex-row items-center px-4 py-5 gap-4 border-b border-ash-3"
          >
            {/* Name: avatar + name */}
            <View style={{ flex: 3 }} className="flex-row items-center gap-2">
              <Bone className="h-8 w-8 rounded-full" />
              <Bone className="h-3 w-32" />
            </View>
            {/* Email */}
            <View style={{ flex: 3 }}>
              <Bone className="h-3 w-40" />
            </View>
            {/* Status */}
            <View style={{ flex: 2 }}>
              <Bone className="h-3 w-16" />
            </View>
            {/* Role badge */}
            <View style={{ flex: 2 }}>
              <Bone className="h-5 w-20 rounded-full" />
            </View>
            {/* Date joined */}
            <View style={{ flex: 2 }}>
              <Bone className="h-3 w-20" />
            </View>
            {/* Actions */}
            <View style={{ flex: 1 }}>
              <Bone className="h-5 w-5 rounded-full" />
            </View>
          </View>
        ))}
      </SkeletonPulse>
    </ScrollView>
  );
}

function UserListingTableError() {
  const { t } = useTranslation();
  return (
    <View className="p-4 flex-col items-center justify-center">
      <Image
        source={require("@/src/assets/images/error.png")}
        style={{ width: 300, height: 300, resizeMode: "contain" }}
      />
      <Text className="mt-2 text-center font-medium text-xl text-crimson">
        {t("ERROR_LOADING_USERS")}
      </Text>
    </View>
  );
}

function UserListingTableEmpty() {
  const { t } = useTranslation();
  return (
    <DataTable.Row>
      <DataTable.Cell style={{ flex: 1 }}>
        <View className="flex-col justify-center items-center gap-2 p-6 w-full">
          <Image
            source={require("@/src/assets/images/empty.png")}
            style={{ width: 300, height: 300, resizeMode: "contain" }}
          />
          <Text className="mt-4 text-center font-medium text-xl text-onyx">
            {t("LABEL_USER_LIST_EMPTY")}
          </Text>
        </View>
      </DataTable.Cell>
    </DataTable.Row>
  );
}

const UserListingTable = ({
  search = "",
  startDate,
  endDate,
  roleFilter,
  statusFilter,
  onFilteredCountChange,
}: UserListingTableProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { orgId, user: currentUser } = useAuth();

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<BlocksUser | null>(null);
  const [confirmAction, setConfirmAction] = useState<
    "activate" | "reactivate" | "deactivate" | "delete" | null
  >(null);
  const [currentPage, setCurrentPage] = useState(0);

  const isFiltering = !!(roleFilter?.length || statusFilter);

  const {
    data: usersData,
    isLoading: isUsersLoading,
    isError: isUsersError,
  } = useUsersQuery(currentPage, search, startDate, endDate);

  const {
    data: allUsersData,
    isLoading: isAllUsersLoading,
    isError: isAllUsersError,
  } = useAllUsersQuery(search, startDate, endDate, isFiltering);

  const { mutate: deactivateUser } = useDeactivateUserMutation();
  const { mutate: deleteUser } = useDeleteUserMutation();
  const { mutate: sendActivationMail } = useSendActivationToUser();
  const { mutate: reactivateUser } = useActivateUserMutation();

  useEffect(() => {
    setCurrentPage(0);
  }, [search, startDate, endDate, roleFilter, statusFilter]);

  const activeData = isFiltering ? allUsersData : usersData;
  const allUsers = activeData?.users ?? [];
  const apiTotalCount = activeData?.totalCount ?? 0;
  const pageSize = activeData?.pageSize ?? 10;
  const filteredUsers = allUsers.filter((blocksUser) => {
    const userRoles = getUserOrgRoles(blocksUser, orgId);

    // Super-admins can't be managed here — hide them from the list.
    if (userRoles.some((role) => role.toLowerCase() === "super-admin"))
      return false;

    const isUserActive = blocksUser?.active;
    const normalizedUserRoles = userRoles.map((r) => r.toLowerCase());
    const passesRoleFilter =
      !roleFilter?.length ||
      roleFilter.some((role) =>
        normalizedUserRoles.includes(role.toLowerCase()),
      );

    const passesStatusFilter =
      !statusFilter ||
      (statusFilter === "active" ? isUserActive : !isUserActive);

    return passesRoleFilter && passesStatusFilter;
  });

  const displayUsers = isFiltering ? filteredUsers : allUsers;
  const totalCount = isFiltering ? filteredUsers.length : apiTotalCount;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const from = totalCount === 0 ? 0 : currentPage * pageSize + 1;
  const to = Math.min((currentPage + 1) * pageSize, totalCount);

  const paginatedUsers = isFiltering ? displayUsers : allUsers;
  useEffect(() => {
    onFilteredCountChange?.(totalCount);
  }, [totalCount, onFilteredCountChange]);

  const openConfirm = (
    blocksUser: BlocksUser,
    action: "activate" | "reactivate" | "deactivate" | "delete",
  ) => {
    setSelectedUser(blocksUser);
    setConfirmAction(action);
    setIsConfirmModalOpen(true);
  };

  const closeConfirm = () => {
    setIsConfirmModalOpen(false);
    setSelectedUser(null);
    setConfirmAction(null);
  };

  const handleConfirm = () => {
    if (!selectedUser) return;
    if (confirmAction === "activate") sendActivationMail(selectedUser.itemId);
    else if (confirmAction === "reactivate") reactivateUser(selectedUser.itemId);
    else if (confirmAction === "deactivate") deactivateUser(selectedUser.itemId);
    else if (confirmAction === "delete") deleteUser(selectedUser.itemId);
    closeConfirm();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page - 1);
  };

  const handleUserPress = (blocksUser: BlocksUser) => {
    router.push({
      pathname: "/profile/[id]",
      params: { id: blocksUser?.itemId },
    });
  };

  if (isFiltering ? isAllUsersLoading : isUsersLoading)
    return <UserListingTableSkeleton />;
  if (isFiltering ? isAllUsersError : isUsersError)
    return <UserListingTableError />;
  return (
    <View className="flex-2 w-full">
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ flexGrow: 1, minWidth: 900 }}
      >
        <DataTable style={{ minWidth: "100%", width: "100%" }}>
          <View className="border-b border-ash-3">
            <DataTable.Header style={{ borderBottomWidth: 0, elevation: 0 }}>
              <DataTable.Title style={{ flex: 3 }}>
                <Text className="text-xs text-onyx font-medium tracking-wide">
                  {t("LABEL_NAME")}
                </Text>
              </DataTable.Title>
              <DataTable.Title style={{ flex: 3 }}>
                <Text className="text-xs text-onyx font-medium tracking-wide">
                  {t("LABEL_EMAIL")}
                </Text>
              </DataTable.Title>
              <DataTable.Title style={{ flex: 2 }}>
                <Text className="text-xs text-onyx font-medium tracking-wide">
                  {t("LABEL_STATUS")}
                </Text>
              </DataTable.Title>
              <DataTable.Title style={{ flex: 2 }}>
                <Text className="text-xs text-onyx font-medium tracking-wide">
                  {t("LABEL_ROLE")}
                </Text>
              </DataTable.Title>
              <DataTable.Title style={{ flex: 2 }}>
                <Text className="text-xs text-onyx font-medium tracking-wide">
                  {t("LABEL_DATE_JOINED")}
                </Text>
              </DataTable.Title>
              <DataTable.Title style={{ flex: 1 }}>
                <Text className="text-xs text-onyx font-medium tracking-wide">
                  {t("LABEL_ACTIONS")}
                </Text>
              </DataTable.Title>
            </DataTable.Header>
          </View>

          {/* ROWS */}
          {paginatedUsers.length === 0 ? (
            <UserListingTableEmpty />
          ) : (
            paginatedUsers.map((blocksUser) => {
              const isUserActive = blocksUser?.active;
              const formattedJoinDate = new Date(
                blocksUser?.createdDate,
              ).toLocaleDateString();
              const userRoles = getUserOrgRoles(blocksUser, orgId);
              const userStatusLabel = isUserActive
                ? t("LABEL_ACTIVE")
                : t("LABEL_INACTIVE");
              const userStatusClassName = isUserActive
                ? "text-jade"
                : "text-crimson";

              const isOwnAccount = blocksUser?.itemId === currentUser?.itemId;

              const userActions = [
                {
                  label: t("BTN_SEND_ACTIVATION"),
                  onPress: () => openConfirm(blocksUser, "activate"),
                  destructive: false,
                  isDisabled: false,
                  rightIcon: (
                    <Send2 size={16} color="var(--color-onyx-default)" />
                  ),
                },
                {
                  // Flips `active` directly (POST /iam/users/activate) — the only
                  // way back for a deactivated user, since "Send activation" just
                  // re-mails an invite they may have already completed.
                  label: t("BTN_REACTIVATE_USER"),
                  onPress: () => openConfirm(blocksUser, "reactivate"),
                  destructive: false,
                  isDisabled: isUserActive,
                  rightIcon: (
                    <UserTick size={16} color="var(--color-onyx-default)" />
                  ),
                },
                {
                  label: t("BTN_DEACTIVATE_USER"),
                  onPress: () => openConfirm(blocksUser, "deactivate"),
                  destructive: false,
                  isDisabled: !isUserActive,
                  rightIcon: (
                    <UserRemove size={16} color="var(--color-onyx-default)" />
                  ),
                  disabledIcon: (
                    <UserRemove size={16} color="var(--color-ash-1)" />
                  ),
                },
                {
                  label: t("BTN_DELETE_USER"),
                  onPress: () => openConfirm(blocksUser, "delete"),
                  destructive: true,
                  isDisabled: false,
                  rightIcon: (
                    <Trash size={16} color="var(--color-crimson-default)" />
                  ),
                },
              ];
              return (
                <DataTable.Row
                  key={blocksUser?.itemId}
                  onPress={() => handleUserPress(blocksUser)}
                  style={{
                    borderBottomWidth: 1,
                    borderBottomColor: "var(--color-ash-3)",
                    paddingVertical: 12,
                  }}
                >
                  <DataTable.Cell style={{ flex: 3 }}>
                    <UserNameCell
                      blocksUser={blocksUser}
                      onPress={handleUserPress}
                    />
                  </DataTable.Cell>

                  <DataTable.Cell style={{ flex: 3 }}>
                    <UserEmailCell email={blocksUser?.email} />
                  </DataTable.Cell>

                  <DataTable.Cell style={{ flex: 2 }}>
                    <Text className={userStatusClassName}>
                      • {userStatusLabel}
                    </Text>
                  </DataTable.Cell>

                  <DataTable.Cell style={{ flex: 2 }}>
                    <RoleBadgeCell roles={userRoles} />
                  </DataTable.Cell>

                  <DataTable.Cell style={{ flex: 2 }}>
                    <Text className="text-slate">{formattedJoinDate}</Text>
                  </DataTable.Cell>

                  <DataTable.Cell style={{ flex: 1 }}>
                    <Pressable onPress={(e) => e.stopPropagation?.()}>
                      <DropDown
                        items={userActions}
                        disabled={isOwnAccount}
                        menuWidth={200}
                      />
                    </Pressable>
                  </DataTable.Cell>
                </DataTable.Row>
              );
            })
          )}
        </DataTable>
      </ScrollView>

      <ScPagination
        currentPage={currentPage + 1}
        totalPages={totalPages}
        totalItems={totalCount}
        from={from}
        to={to}
        onPageChange={handlePageChange}
      />

      <ConfirmModal
        visible={isConfirmModalOpen}
        title={
          confirmAction === "activate"
            ? t("QUEST_SEND_ACTIVATION")
            : confirmAction === "reactivate"
              ? t("QUEST_REACTIVATE_ACCOUNT", { name: selectedUser?.firstName })
              : confirmAction === "delete"
              ? t("QUEST_DELETE_ACCOUNT", { name: selectedUser?.firstName })
              : t("QUEST_DEACTIVATE_ACCOUNT", { name: selectedUser?.firstName })
        }
        description={
          confirmAction === "activate"
            ? t("LABEL_DESC_SEND_ACTIVATION")
            : confirmAction === "reactivate"
              ? t("LABEL_DESC_REACTIVATE_ACCOUNT")
              : confirmAction === "delete"
              ? t("LABEL_DESC_DELETE_ACCOUNT")
              : t("LABEL_DESC_DEACTIVATE_ACCOUNT")
        }
        confirmText={
          confirmAction === "activate"
            ? t("BTN_SEND_ACTIVATION")
            : confirmAction === "reactivate"
              ? t("BTN_REACTIVATE")
              : confirmAction === "delete"
              ? t("BTN_DELETE")
              : t("BTN_DEACTIVATE")
        }
        cancelText={t("BTN_CANCEL")}
        onCancel={closeConfirm}
        onConfirm={handleConfirm}
      />
    </View>
  );
};

export default UserListingTable;
